import { tenantRoleSchema, type TenantRole } from '@lib/enums/tenant-role.enum'
import type { TeamMemberDto } from '@lib/dto/team.dto'
import type { Uuid } from '@lib/validators/params'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ROLE_LABELS, ROLE_OPTIONS, ROLE_TONES } from './roles'

interface MemberTableProps {
  members: TeamMemberDto[]
  /** Owners see role pickers and Remove; everyone else sees the list. */
  canManage: boolean
  currentUserId?: Uuid
  busyUserId: Uuid | null
  onChangeRole?: (userId: Uuid, role: TenantRole) => void
  onRemove?: (member: TeamMemberDto) => void
}

function RoleCell({
  member,
  canManage,
  busy,
  onChangeRole,
}: {
  member: TeamMemberDto
  canManage: boolean
  busy: boolean
  onChangeRole?: (userId: Uuid, role: TenantRole) => void
}) {
  if (!canManage || !onChangeRole) {
    return <Badge tone={ROLE_TONES[member.role]}>{ROLE_LABELS[member.role]}</Badge>
  }
  return (
    <Select
      aria-label={`Role for ${member.name}`}
      wrapperClassName="w-36"
      className="h-control-sm"
      value={member.role}
      disabled={busy}
      onChange={(event) => {
        const next = tenantRoleSchema.safeParse(event.target.value)
        if (next.success) onChangeRole(member.userId, next.data)
      }}
    >
      {ROLE_OPTIONS.map((option) => (
        <option key={option} value={option}>
          {ROLE_LABELS[option]}
        </option>
      ))}
    </Select>
  )
}

export function MemberTable({
  members,
  canManage,
  currentUserId,
  busyUserId,
  onChangeRole,
  onRemove,
}: MemberTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Joined</TableHead>
          {canManage ? (
            <TableHead>
              <span className="sr-only">Actions</span>
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => (
          <TableRow key={member.userId}>
            <TableCell className="font-medium">
              {member.name}
              {member.userId === currentUserId ? (
                <span className="ml-1.5 text-caption font-normal text-muted-foreground">you</span>
              ) : null}
            </TableCell>
            <TableCell className="text-muted-foreground">{member.email}</TableCell>
            <TableCell>
              <RoleCell
                member={member}
                canManage={canManage}
                busy={busyUserId === member.userId}
                {...(onChangeRole ? { onChangeRole } : {})}
              />
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {new Date(member.joinedAt).toLocaleDateString()}
            </TableCell>
            {canManage ? (
              <TableCell className="text-right">
                {onRemove ? (
                  <Button
                    variant="destructive-quiet"
                    size="sm"
                    disabled={busyUserId === member.userId}
                    onClick={() => onRemove(member)}
                  >
                    Remove
                  </Button>
                ) : null}
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
